import { Component, Input } from '@angular/core';

/**
 * Pre-flight review of an RFQ imported from another procurement platform.
 *
 * An RFQ arriving as a PDF is a document written for a person, so reading it
 * back into data is lossy: table headings bleed into rows, a truncation notice
 * can look like a part, and the part number is written inside the description
 * rather than in its own column. The buyer sees what the import understood
 * BEFORE anything is written, which is why the RFQ lands in Draft.
 *
 * The panel is deliberately calm rather than alarming — a skipped header row
 * is normal for this kind of document, not an error the buyer caused.
 */
@Component({
  selector: 'app-import-review',
  templateUrl: './import-review.component.html',
  styleUrls: ['./import-review.component.css'],
})
export class ImportReviewComponent {
  /** The report from /rfq/reviewImportedRfq. */
  @Input() review: any = null;

  /** Collapsed by default; the headline totals carry the meaning. */
  showLines = false;

  /**
   * Built here rather than in the template: the toggle is a flex row with a
   * gap, so an inline <span> for the plural rendered as "line  s  we read".
   */
  get toggleLabel(): string {
    const n = this.lines?.length || 0;
    return `${this.showLines ? 'Hide' : 'Show'} the ${n} line${n === 1 ? '' : 's'} we read`;
  }

  get lines(): any[] {
    return this.review?.lines || [];
  }

  get readableLines(): any[] {
    return this.lines.filter((l) => l.status === 'OK');
  }

  get attentionLines(): any[] {
    return this.lines.filter((l) => l.status !== 'OK');
  }

  get hasAttention(): boolean {
    return this.attentionLines.length > 0;
  }

  /** Chip colour by what the line needs, not by severity. */
  chipClass(line: any): string {
    if (line.status === 'NEEDS_REVIEW') return 'ir-chip--check';
    if (line.status === 'UNREADABLE') return 'ir-chip--skip';
    return line.classification === 'SPARE_PART' ? 'ir-chip--spare' : 'ir-chip--store';
  }

  chipLabel(line: any): string {
    if (line.status === 'NEEDS_REVIEW') return 'Check';
    if (line.status === 'UNREADABLE') return 'Skipped';
    return line.classification === 'SPARE_PART' ? 'Spare part' : 'Consumable';
  }
}
